/**
 * Локальный слой демонстрационных действий.
 *
 * Контракт формата — `prototype/ROLES-SPEC.md`: действия персон НЕ меняют
 * фикстуры, они сохраняются отдельно в `localStorage['astra-overlay-v1-A|B']`:
 *
 * ```json
 * { "executions":    [{ "obligation_id", "on", "by", "on_behalf", "note" }],
 *   "verifications": [{ "execution_ref", "verdict", "reason", "by" }],
 *   "campaigns":     [ ... ],
 *   "assignments":   [{ "id", "title", "body", "level", "ref", "assignee",
 *                       "due", "status", "created_by", "events" }] }
 * ```
 *
 * Правила слоя:
 * - слой сохраняется в браузере по набору данных и помечается как демо-ввод;
 * - запись НИКОГДА не перезаписывает и не удаляет чужую: и отчёт, и суждение
 *   проверяющего остаются рядом, конфликт показывается обеими записями;
 * - чтение обязано пережить чужой формат: незнакомые поля сохраняются,
 *   битый JSON не роняет экран, приватный режим браузера — тоже.
 *
 * `execution_ref` у проверки — это `id` записи исполнения. Своё `id` запись
 * получает при записи (`appendExecutions`) и уже никогда его не меняет: без
 * него повторный отчёт после опровержения унаследовал бы чужое суждение.
 * Записи старого формата без `id` читаются как `id = obligation_id` — ровно
 * тот ключ, которым их проверки уже подписаны, поэтому прежний слой в браузере
 * продолжает читаться и после этой правки.
 */

import { useSyncExternalStore } from 'react';
import { validDate } from '../astra/model';
import { defineMessages } from '../../i18n/define';
import { tr } from '../../i18n/runtime';

const m = defineMessages({
  unknownAuthor: {
    id: 'auth.overlay.unknownAuthor',
    defaultMessage: 'неизвестный автор',
    description: 'Подпись автора записи в сценарии, когда автор не указан (строчная фраза)',
  },
  unknownVerifier: {
    id: 'auth.overlay.unknownVerifier',
    defaultMessage: 'неизвестный проверяющий',
    description: 'Подпись проверяющего в сценарии, когда он не указан (строчная фраза)',
  },
  noReason: {
    id: 'auth.overlay.noReason',
    defaultMessage: 'причина не указана',
    description: 'Причина проверки в сценарии, когда она не указана (строчная фраза)',
  },
  noAssignee: {
    id: 'auth.overlay.noAssignee',
    defaultMessage: 'ответственный не назван',
    description: 'Ответственный по поручению в сценарии, когда он не назван (строчная фраза)',
  },
  assignmentObject: {
    id: 'auth.overlay.assignmentObject',
    defaultMessage: 'поручение должно быть объектом',
    description: 'Причина отказа при загрузке файла сценария (демонстрационный слой): запись поручения не является объектом',
  },
  assignmentFields: {
    id: 'auth.overlay.assignmentFields',
    defaultMessage: 'у поручения нужны идентификатор, заголовок и автор',
    description: 'Причина отказа при загрузке файла сценария (демонстрационный слой): в поручении не хватает обязательных полей',
  },
  assignmentNoHistory: {
    id: 'auth.overlay.assignmentNoHistory',
    defaultMessage: 'у поручения отсутствует история создания',
    description: 'Причина отказа при загрузке файла сценария (демонстрационный слой): у поручения нет истории',
  },
  eventBad: {
    id: 'auth.overlay.eventBad',
    defaultMessage: 'некорректная дата или автор события поручения',
    description: 'Причина отказа при загрузке файла сценария (демонстрационный слой): событие поручения с неверной датой или без автора',
  },
  historyStart: {
    id: 'auth.overlay.historyStart',
    defaultMessage: 'история поручения должна начинаться с его создания указанным автором',
    description: 'Причина отказа при загрузке файла сценария (демонстрационный слой): история поручения начинается не с создания',
  },
  eventsOrder: {
    id: 'auth.overlay.eventsOrder',
    defaultMessage: 'даты событий поручения идут в обратном порядке',
    description: 'Причина отказа при загрузке файла сценария (демонстрационный слой): события поручения идут не по порядку дат',
  },
  noteEmpty: {
    id: 'auth.overlay.noteEmpty',
    defaultMessage: 'пустое примечание в истории поручения',
    description: 'Причина отказа при загрузке файла сценария (демонстрационный слой): в истории поручения пустое примечание',
  },
  stageOrder: {
    id: 'auth.overlay.stageOrder',
    defaultMessage: 'нарушен порядок этапов, отсутствует основание или отчёт проверяет его автор',
    description: 'Причина отказа при загрузке файла сценария (демонстрационный слой): нарушен порядок этапов поручения, нет основания или проверяет автор отчёта',
  },
  dueBad: {
    id: 'auth.overlay.dueBad',
    defaultMessage: 'некорректный срок поручения',
    description: 'Причина отказа при загрузке файла сценария (демонстрационный слой): срок поручения не читается как дата',
  },
  baselineOut: {
    id: 'auth.overlay.baselineOut',
    defaultMessage: 'исходные показатели поручения находятся вне доступного периода',
    description: 'Причина отказа при загрузке файла сценария (демонстрационный слой): исходные показатели поручения за пределами доступного периода',
  },
  rowNoObligation: {
    id: 'auth.overlay.rowNoObligation',
    defaultMessage: 'запись без обязательства',
    description: 'Причина отклонения записи отчёта при вводе: не указана работа (строчная фраза)',
  },
  rowNoDate: {
    id: 'auth.overlay.rowNoDate',
    defaultMessage: 'дата выполнения не заполнена или не читается',
    description: 'Причина отклонения записи отчёта при вводе: дата выполнения пуста или неверна (строчная фраза)',
  },
  notJson: {
    id: 'auth.overlay.notJson',
    defaultMessage: 'файл не разбирается как JSON',
    description: 'Причина отказа при загрузке файла сценария (демонстрационный слой): файл повреждён',
  },
  notObject: {
    id: 'auth.overlay.notObject',
    defaultMessage: 'формат файла не похож на слой сценария: ожидался объект',
    description: 'Причина отказа при загрузке файла сценария (демонстрационный слой): в файле не объект',
  },
  otherTrack: {
    id: 'auth.overlay.otherTrack',
    defaultMessage: 'сценарий относится к набору {track}; сначала переключите набор данных',
    description: 'Причина отказа при загрузке файла сценария (демонстрационный слой): сценарий для другого набора данных; {track} — буква набора (A или B)',
  },
  badVersion: {
    id: 'auth.overlay.badVersion',
    defaultMessage: 'неподдерживаемый формат или версия сценария',
    description: 'Причина отказа при загрузке файла сценария (демонстрационный слой): формат или версия файла не поддерживаются',
  },
  noSections: {
    id: 'auth.overlay.noSections',
    defaultMessage: 'в файле нет ни одного раздела слоя (executions/verifications/campaigns/assignments)',
    description: 'Причина отказа при загрузке файла сценария (демонстрационный слой): в файле нет ни одного известного раздела; в скобках — служебные имена разделов, не переводить',
  },
  execBad: {
    id: 'auth.overlay.execBad',
    defaultMessage: 'отчёт содержит некорректную дату или идентификатор обязательства',
    description: 'Причина отказа при загрузке файла сценария (демонстрационный слой): в отчёте неверная дата или работа',
  },
  assignmentPrefix: {
    id: 'auth.overlay.assignmentPrefix',
    defaultMessage: 'Поручение: {reason}',
    description: 'Причина отказа при загрузке файла сценария (демонстрационный слой): ошибка в поручении; {reason} — готовая причина',
  },
  assignmentDuplicate: {
    id: 'auth.overlay.assignmentDuplicate',
    defaultMessage: 'повторяется идентификатор поручения',
    description: 'Причина отказа при загрузке файла сценария (демонстрационный слой): два поручения с одним идентификатором',
  },
  baselineOtherTrack: {
    id: 'auth.overlay.baselineOtherTrack',
    defaultMessage: 'исходные показатели поручения относятся к другому набору данных',
    description: 'Причина отказа при загрузке файла сценария (демонстрационный слой): исходные показатели поручения от другого набора данных',
  },
});

// i18n-ignore: значение пишется в слой сценария и сравнивается по равенству, поэтому хранится на языке-источнике; на экране его подменяет overlayText()
const UNKNOWN_AUTHOR = 'неизвестный автор';
// i18n-ignore: то же, что UNKNOWN_AUTHOR
const UNKNOWN_VERIFIER = 'неизвестный проверяющий';
// i18n-ignore: то же, что UNKNOWN_AUTHOR
const NO_REASON = 'причина не указана';
// i18n-ignore: то же, что UNKNOWN_AUTHOR
const NO_ASSIGNEE = 'ответственный не назван';

/**
 * Подпись для экрана: служебные значения слоя («неизвестный автор»…) на языке
 * интерфейса, остальной текст — как есть.
 */
export function overlayText(value: string): string {
  if (value === UNKNOWN_AUTHOR) return tr(m.unknownAuthor);
  if (value === UNKNOWN_VERIFIER) return tr(m.unknownVerifier);
  if (value === NO_REASON) return tr(m.noReason);
  if (value === NO_ASSIGNEE) return tr(m.noAssignee);
  return value;
}


export const OVERLAY_KEY = 'astra-overlay-v1';
let activeTrack = (() => { try { return window.localStorage.getItem('proto-track') === 'A' ? 'A' : 'B'; } catch { return 'B'; } })();
function storageKey() {
  return `${OVERLAY_KEY}-${activeTrack}`;
}
export function setOverlayTrack(track: 'A' | 'B') {
  activeTrack = track;
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(OVERLAY_EVENT));
}

/**
 * localStorage не рассылает `storage` в собственной вкладке, а прототип
 * весь живёт в одной. Поэтому запись сама объявляет о себе этим событием.
 */
export const OVERLAY_EVENT = 'proto-overlay-change';

/* ------------------------------------------------------------------ типы */

export interface OverlayExecution {
  /** Обязательство фикстуры, по которому внесён отчёт. */
  obligation_id: string;
  /** Дата выполнения работы (YYYY-MM-DD), утверждение автора записи. */
  on: string;
  /** Аккаунт, внёсший запись. */
  by: string;
  /** Хозяйство, от имени которого внесена запись; null — автор пишет за себя. */
  on_behalf: string | null;
  note: string | null;
  /**
   * Идентификатор записи, которым на неё ссылается проверка. Проставляется
   * при записи и выводится при чтении старого слоя (там это `obligation_id`).
   */
  id?: string;
  /** Момент ввода (ISO) — только для показа «внесено в этой сессии». */
  at?: string;
}

export type OverlayVerdict = 'confirmed' | 'refuted';

export interface OverlayVerification {
  /** `id` записи исполнения; у отчёта среза это сам `obligation_id`. */
  execution_ref: string;
  verdict: OverlayVerdict;
  /** Причина обязательна: суждение без причины — не суждение. */
  reason: string;
  by: string;
  at?: string;
}

/**
 * Кампании принадлежат кабинету диспетчера: слой их только переносит.
 * `recurrence` — опциональная пометка периодической серии («раз в месяц» и
 * т.п.), которую пишет и читает только /ops/campaigns; обычная разовая
 * кампания это поле не проставляет. Индекс-сигнатура оставляет запись
 * свободной: чужие поля не мешают и не требуют объявления здесь.
 */
export interface OverlayCampaign {
  /** Периодичность мок-серии словами («раз в неделю» и т.п.); demo-only. */
  recurrence?: string;
  [key: string]: unknown;
}

/* ------------------------------------------------------------ поручения */

/**
 * Четыре состояния поручения. Оси РАЗДЕЛЬНЫ по канону
 * `docs/design-system/application-patterns.md` («assignment, response, action
 * and check are separate events»): `done` — ответственный отчитался о
 * выполнении, `checked` — поручение проверено. Одно не повышается до другого.
 */
export type AssignmentStatus = 'open' | 'in_progress' | 'done' | 'checked';

/** Порядок состояний. Перескок через ступень в прототипе не предусмотрен. */
export const ASSIGNMENT_FLOW: AssignmentStatus[] = ['open', 'in_progress', 'done', 'checked'];

/** Следующее состояние или null, если поручение дошло до конца цепочки. */
export function nextAssignmentStatus(status: AssignmentStatus): AssignmentStatus | null {
  const i = ASSIGNMENT_FLOW.indexOf(status);
  return i < 0 || i === ASSIGNMENT_FLOW.length - 1 ? null : ASSIGNMENT_FLOW[i + 1]!;
}

/**
 * Событие в истории поручения. Каждый переход пишет СВОЁ событие: история
 * дописывается и никогда не переписывается, поэтому «отчитался» и «проверено»
 * остаются двумя разными записями с разными авторами и датами.
 */
export interface AssignmentEvent {
  /** Дата события (YYYY-MM-DD): срез прототипа, а не системные часы. */
  date: string;
  /** Что произошло: `created` либо имя нового состояния, либо `note`. */
  kind: string;
  by: string;
  note: string | null;
}

/** Уровень предмета поручения — тот же словарь, что у очереди внимания. */
export type AssignmentLevel = 'district' | 'massif' | 'farm' | 'contour' | 'work';

/** К чему привязано поручение. Пустая ссылка допустима: это честное «адреса нет». */
export interface AssignmentRef {
  cycle_id?: string;
  massif_id?: string;
  work_id?: string;
  farm_id?: string;
  contour_id?: string;
}

export interface OverlayAssignment {
  id: string;
  title: string;
  body: string;
  level: AssignmentLevel;
  ref: AssignmentRef;
  /** Роль или служба, которой поручено: свободный текст, не аккаунт. */
  assignee: string;
  /** Срок (YYYY-MM-DD); null — срок не назначен, и это называется словами. */
  due: string | null;
  status: AssignmentStatus;
  created_by: string;
  events: AssignmentEvent[];
  /** Откуда поручение: позиция очереди внимания или ручной ввод. */
  source?: 'attention' | 'manual';
  /** `id` позиции очереди внимания, если поручение выросло из неё. */
  source_id?: string;
  /** Observed metrics when the assignment was created; immutable and local to this track. */
  baseline?: AssignmentBaseline;
}

export interface AssignmentBaseline {
  as_of: string; track: string; definition_version: string;
  total: number; reported: number; verified: number; overdue: number; water_gap_m3: number;
}
export function parseAssignmentBaseline(raw: unknown): AssignmentBaseline | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const b = raw as AssignmentBaseline;
  if (!validDate(b.as_of) || !['A', 'B'].includes(b.track) || b.definition_version !== 'astra-analysis-v1') return undefined;
  if (['total', 'reported', 'verified', 'overdue', 'water_gap_m3'].some(k => !Number.isFinite(b[k as keyof AssignmentBaseline]) || Number(b[k as keyof AssignmentBaseline]) < 0)) return undefined;
  if ([b.total,b.reported,b.verified,b.overdue].some(n => !Number.isInteger(n)) || b.reported > b.total || b.verified > b.reported || b.overdue > b.total - b.reported) return undefined;
  return { as_of: b.as_of, track: b.track, definition_version: b.definition_version, total: b.total, reported: b.reported, verified: b.verified, overdue: b.overdue, water_gap_m3: b.water_gap_m3 };
}

export interface ProtoOverlay {
  executions: OverlayExecution[];
  verifications: OverlayVerification[];
  campaigns: OverlayCampaign[];
  assignments: OverlayAssignment[];
}

export const EMPTY_OVERLAY: ProtoOverlay = Object.freeze({
  executions: [],
  verifications: [],
  campaigns: [],
  assignments: [],
}) as ProtoOverlay;

/* --------------------------------------------------------------- разбор */

function str(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

function nullableStr(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/**
 * Нормализуется только то, без чего запись нечитаема. Остальные поля
 * переносятся как есть: чужой агент вправе положить в запись своё.
 */
function parseExecution(raw: unknown): OverlayExecution | null {
  if (!raw || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  const obligationId = str(row.obligation_id) ?? str(row.obligationId);
  const on = str(row.on) ?? str(row.date);
  if (!obligationId || !on || !validDate(on)) return null;
  return {
    ...row,
    obligation_id: obligationId,
    on,
    by: str(row.by) ?? UNKNOWN_AUTHOR,
    on_behalf: nullableStr(row.on_behalf) ?? nullableStr(row.onBehalf),
    note: nullableStr(row.note),
    // Запись без `id` пришла из старого слоя: там ссылкой служил сам
    // `obligation_id`, и подменять его новым ключом значило бы оторвать
    // от неё уже записанные проверки.
    id: str(row.id) ?? obligationId,
    at: str(row.at) ?? undefined,
  };
}

function parseVerification(raw: unknown): OverlayVerification | null {
  if (!raw || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  const ref = str(row.execution_ref) ?? str(row.executionRef) ?? str(row.obligation_id);
  const verdict = str(row.verdict);
  if (!ref || (verdict !== 'confirmed' && verdict !== 'refuted')) return null;
  return {
    ...row,
    execution_ref: ref,
    verdict,
    // Причина обязательна по правилам честности; отсутствие причины не
    // прячется, а называется словами — иначе суждение выглядит обоснованным.
    reason: str(row.reason) ?? NO_REASON,
    by: str(row.by) ?? UNKNOWN_VERIFIER,
    at: str(row.at) ?? undefined,
  };
}

function isStatus(value: unknown): value is AssignmentStatus {
  return (
    value === 'open' || value === 'in_progress' || value === 'done' || value === 'checked'
  );
}

function isLevel(value: unknown): value is AssignmentLevel {
  return (
    value === 'district' ||
    value === 'massif' ||
    value === 'farm' ||
    value === 'contour' ||
    value === 'work'
  );
}

function parseEvent(raw: unknown): AssignmentEvent | null {
  if (!raw || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  const date = str(row.date) ?? str(row.on);
  const kind = str(row.kind);
  if (!date || !kind) return null;
  return {
    ...row,
    date,
    kind,
    by: str(row.by) ?? UNKNOWN_AUTHOR,
    note: nullableStr(row.note),
  };
}

function parseRef(raw: unknown): AssignmentRef {
  if (!raw || typeof raw !== 'object') return {};
  const row = raw as Record<string, unknown>;
  const ref: AssignmentRef = {};
  const cycle = str(row.cycle_id);
  const massif = str(row.massif_id);
  const work = str(row.work_id);
  const farm = str(row.farm_id);
  const contour = str(row.contour_id);
  if (cycle) ref.cycle_id = cycle;
  if (massif) ref.massif_id = massif;
  if (work) ref.work_id = work;
  if (farm) ref.farm_id = farm;
  if (contour) ref.contour_id = contour;
  return ref;
}

/** Imported histories obey the same sequence and evidence rules as interactive actions. */
export function validateAssignmentHistory(raw: unknown, snapshot = '2026-08-26'): string | null {
  if (!raw || typeof raw !== 'object') return tr(m.assignmentObject);
  const row = raw as Record<string, unknown>;
  if (!str(row.id) || !str(row.title) || !str(row.created_by)) return tr(m.assignmentFields);
  if (!Array.isArray(row.events) || !row.events.length) return tr(m.assignmentNoHistory);
  let status: AssignmentStatus = 'open';
  const history: AssignmentEvent[] = [];
  for (let i = 0; i < row.events.length; i++) {
    const event = parseEvent(row.events[i]);
    if (!event || !validDate(event.date) || event.date > snapshot || event.by === UNKNOWN_AUTHOR || !event.by.trim()) return tr(m.eventBad);
    if (i === 0) {
      if (event.kind !== 'created' || event.by !== row.created_by) return tr(m.historyStart);
    } else {
      if (event.date < history[i-1]!.date) return tr(m.eventsOrder);
      if (event.kind === 'note') {
        if (!event.note?.trim()) return tr(m.noteEmpty);
      } else {
        if (!isStatus(event.kind) || !canAdvanceAssignment({ status, events:history } as OverlayAssignment, event.kind, event.by, event.note)) return tr(m.stageOrder);
        status = event.kind;
      }
    }
    history.push(event);
  }
  if (row.due && (typeof row.due !== 'string' || !validDate(row.due))) return tr(m.dueBad);
  if (row.baseline) {
    const baseline = parseAssignmentBaseline(row.baseline);
    if (!baseline || baseline.as_of > snapshot || baseline.as_of > history[0]!.date) return tr(m.baselineOut);
  }
  return null;
}

/**
 * Поручение без заголовка нечитаемо, всё остальное восстановимо. Состояние
 * сверяется с историей: если события есть, последнее из них и есть текущее
 * состояние — иначе чужая правка одного поля рассказывала бы не то, что лог.
 */
function parseAssignment(raw: unknown): OverlayAssignment | null {
  if (!raw || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  const id = str(row.id);
  const title = str(row.title);
  if (!id || !title) return null;

  const events = Array.isArray(row.events)
    ? row.events.map(parseEvent).filter((e): e is AssignmentEvent => e !== null)
    : [];

  const lastStatus = [...events].reverse().find((e) => isStatus(e.kind))?.kind;
  const status = isStatus(lastStatus)
    ? lastStatus
    : isStatus(row.status)
      ? row.status
      : 'open';

  const source = row.source === 'attention' || row.source === 'manual' ? row.source : undefined;

  return {
    ...row,
    id,
    title,
    body: str(row.body) ?? '',
    level: isLevel(row.level) ? row.level : 'district',
    ref: parseRef(row.ref),
    assignee: str(row.assignee) ?? NO_ASSIGNEE,
    due: nullableStr(row.due),
    status,
    created_by: str(row.created_by) ?? str(row.createdBy) ?? UNKNOWN_AUTHOR,
    events,
    baseline: parseAssignmentBaseline(row.baseline),
    ...(source ? { source } : {}),
    ...(str(row.source_id) ? { source_id: str(row.source_id) as string } : {}),
  };
}

function parseOverlay(raw: string | null): ProtoOverlay {
  if (!raw) return EMPTY_OVERLAY;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return EMPTY_OVERLAY;
  }
  if (!parsed || typeof parsed !== 'object') return EMPTY_OVERLAY;
  const box = parsed as Record<string, unknown>;
  const executions = Array.isArray(box.executions)
    ? box.executions.map(parseExecution).filter((row): row is OverlayExecution => row !== null)
    : [];
  const verifications = Array.isArray(box.verifications)
    ? box.verifications
        .map(parseVerification)
        .filter((row): row is OverlayVerification => row !== null)
    : [];
  const campaigns = Array.isArray(box.campaigns)
    ? box.campaigns.filter(
        (row): row is OverlayCampaign => Boolean(row) && typeof row === 'object',
      )
    : [];
  const assignments = Array.isArray(box.assignments)
    ? box.assignments.map(parseAssignment).filter((row): row is OverlayAssignment => row !== null)
    : [];
  return { executions, verifications, campaigns, assignments };
}

/* -------------------------------------------------------------- хранилище */

function readRaw(): string | null {
  const key = storageKey();
  if (memoryOnly.has(key)) return memoryRaw.get(key) ?? null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return memoryRaw.get(key) ?? null;
  }
}

/** Приватный режим/недоступное хранилище: слой живёт в памяти вкладки. */
const memoryRaw = new Map<string, string>();
const memoryOnly = new Set<string>();

let cachedRaw: string | null = null;
let cached: ProtoOverlay = EMPTY_OVERLAY;

/**
 * Ссылка на результат стабильна, пока не менялась строка хранилища:
 * `useSyncExternalStore` сравнивает снимки по ссылке и без кэша зациклился бы.
 */
export function readOverlay(): ProtoOverlay {
  const raw = readRaw();
  if (raw === cachedRaw) return cached;
  cachedRaw = raw;
  cached = parseOverlay(raw);
  return cached;
}

export function writeOverlay(next: ProtoOverlay): ProtoOverlay {
  const raw = JSON.stringify(next);
  const key = storageKey();
  memoryRaw.set(key, raw);
  try {
    window.localStorage.setItem(key, raw);
    memoryOnly.delete(key);
  } catch {
    memoryOnly.add(key);
  }
  cachedRaw = raw;
  cached = parseOverlay(raw);
  try {
    window.dispatchEvent(new CustomEvent(OVERLAY_EVENT));
  } catch {
    // Среда без CustomEvent (SSR-дым) — подписчиков там всё равно нет.
  }
  return cached;
}

/**
 * Ключ записи исполнения: обязательство, момент ввода и автор. Он читается
 * глазами и не требует генератора случайностей, а повторный отчёт по тому же
 * обязательству отличается от прежнего моментом ввода.
 */
export function executionIdFor(obligationId: string, at: string, by: string): string {
  return `x|${obligationId}|${at}|${by}`;
}

export interface AppendExecutionsResult {
  overlay: ProtoOverlay;
  /** Записи, реально попавшие в слой, уже со своими `id` и `at`. */
  written: OverlayExecution[];
  /** Отклонённые записи и причина — по одной на запись. */
  rejected: { row: OverlayExecution; reason: string }[];
}

/**
 * Добавление НИКОГДА не трогает чужие разделы слоя.
 *
 * Запись с непринимаемой датой не сохраняется молча: она возвращается вызвавшему
 * экрану вместе с причиной, чтобы форма показала отказ словами, а не отрапортовала
 * об отчёте, которого в слое нет.
 */
export function appendExecutions(rows: OverlayExecution[]): AppendExecutionsResult {
  const current = readOverlay();
  if (rows.length === 0) return { overlay: current, written: [], rejected: [] };

  const stamp = new Date().toISOString();
  const taken = new Set(current.executions.map((row) => row.id ?? row.obligation_id));
  const written: OverlayExecution[] = [];
  const rejected: { row: OverlayExecution; reason: string }[] = [];

  for (const row of rows) {
    if (!row.obligation_id.trim()) {
      rejected.push({ row, reason: tr(m.rowNoObligation) });
      continue;
    }
    if (!validDate(row.on)) {
      rejected.push({ row, reason: tr(m.rowNoDate) });
      continue;
    }
    const at = row.at ?? stamp;
    const by = row.by.trim() === '' ? UNKNOWN_AUTHOR : row.by;
    let id = row.id ?? executionIdFor(row.obligation_id, at, by);
    // Два ввода в одну миллисекунду — не повод склеить две записи в одну.
    for (let n = 2; taken.has(id); n += 1) id = `${executionIdFor(row.obligation_id, at, by)}~${n}`;
    taken.add(id);
    written.push({ ...row, id, at, by });
  }

  if (written.length === 0) return { overlay: current, written: [], rejected };
  return {
    overlay: writeOverlay({ ...current, executions: [...current.executions, ...written] }),
    written,
    rejected,
  };
}

export function appendVerification(row: OverlayVerification): ProtoOverlay {
  const current = readOverlay();
  return writeOverlay({
    ...current,
    verifications: [...current.verifications, row],
  });
}

export function appendCampaign(row: OverlayCampaign): ProtoOverlay {
  const current = readOverlay();
  return writeOverlay({ ...current, campaigns: [...current.campaigns, row] });
}

export function appendAssignment(row: OverlayAssignment): ProtoOverlay {
  const current = readOverlay();
  return writeOverlay({ ...current, assignments: [...current.assignments, row] });
}

/**
 * Перевод поручения на следующую ступень. Событие ДОПИСЫВАЕТСЯ, прошлые
 * записи не трогаются: `status` — это проекция последнего события, а не
 * самостоятельная правда. Чужие поручения и чужие разделы слоя не меняются.
 */
export function canAdvanceAssignment(row: OverlayAssignment, to: AssignmentStatus, by: string, note?: string | null): boolean {
  if (nextAssignmentStatus(row.status) !== to || !by.trim()) return false;
  if ((to === 'done' || to === 'checked') && !note?.trim()) return false;
  if (to === 'checked') {
    const reporter = [...row.events].reverse().find(e => e.kind === 'done')?.by;
    if (!reporter || reporter === by) return false;
  }
  return true;
}

/**
 * Может ли `by` вынести суждение по этому отчёту.
 *
 * Тот же запрет, что и у поручений (`canAdvanceAssignment`, ступень `checked`):
 * автор отчёта не проверяет собственный отчёт. Подпись под своим утверждением
 * не добавляет к нему ни одного независимого наблюдения, а на экране выглядит
 * как проверка — поэтому она не записывается вовсе.
 *
 * Автором считается тот, кто внёс запись (`by`), а не тот, за кого она внесена
 * (`on_behalf`): диспетчер, отчитавшийся за хозяйство, проверить эту запись
 * тоже не может.
 */
export function canJudgeExecution(
  execution: Pick<OverlayExecution, 'by'> | null | undefined,
  by: string,
): boolean {
  if (!by.trim()) return false;
  if (!execution) return true;
  return execution.by.trim() !== by.trim();
}

export function advanceAssignment(
  id: string,
  to: AssignmentStatus,
  by: string,
  date: string,
  note?: string | null,
): ProtoOverlay {
  const current = readOverlay();
  const assignments = current.assignments.map((row) =>
    row.id === id && canAdvanceAssignment(row, to, by, note)
      ? {
          ...row,
          status: to,
          events: [...row.events, { date, kind: to, by, note: note?.trim() || null }],
        }
      : row,
  );
  return writeOverlay({ ...current, assignments });
}

export function clearOverlay(): ProtoOverlay {
  return writeOverlay({ executions: [], verifications: [], campaigns: [], assignments: [] });
}

/* ------------------------------------------------------------ экспорт/импорт */

/**
 * Формат файла сценария: слой обёрнут версией и датой выгрузки, чтобы файл
 * читался отдельно от `sessionStorage` — на другой машине, в другой вкладке,
 * спустя недели. Дата здесь — момент выгрузки файла, а не дата среза мира.
 */
const OVERLAY_EXPORT_KIND = 'agroai-proto-overlay';
const OVERLAY_EXPORT_VERSION = 1;

interface OverlayExportFile {
  kind: typeof OVERLAY_EXPORT_KIND;
  version: typeof OVERLAY_EXPORT_VERSION;
  track: string;
  exported_at: string;
  overlay: ProtoOverlay;
}

/** Слой сессии целиком, с версией и датой выгрузки — файл сценария для показа. */
export function exportOverlay(): string {
  const file: OverlayExportFile = {
    kind: OVERLAY_EXPORT_KIND,
    version: OVERLAY_EXPORT_VERSION,
    track: activeTrack,
    exported_at: new Date().toISOString(),
    overlay: readOverlay(),
  };
  return JSON.stringify(file, null, 2);
}

export interface ImportOverlayResult {
  ok: boolean;
  /** Причина отказа — только при `ok: false`: молчаливый отказ не годится. */
  reason?: string;
}

/** Хотя бы один из четырёх разделов слоя присутствует как массив (пустой считается). */
function looksLikeOverlay(value: Record<string, unknown>): boolean {
  return (
    Array.isArray(value.executions) ||
    Array.isArray(value.verifications) ||
    Array.isArray(value.campaigns) ||
    Array.isArray(value.assignments)
  );
}

/**
 * Загрузка сценария из файла. Форма проверяется, прежде чем слой заменяется:
 * посторонний JSON без единого раздела слоя отклоняется словами, а не тихо
 * превращается в пустой слой. Разбор записей переиспользует `parseOverlay` —
 * те же правила прощения чужого формата, что и при обычном чтении хранилища.
 *
 * Успешная загрузка ЗАМЕНЯЕТ слой ЦЕЛИКОМ (не сливает с прежним вводом):
 * сценарий описывает мир сессии полностью, а не дополняет то, что было.
 */
export function importOverlay(json: string, validateExecution?: (row: OverlayExecution) => string | null, snapshot = '2026-08-26'): ImportOverlayResult {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return { ok: false, reason: tr(m.notJson) };
  }

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { ok: false, reason: tr(m.notObject) };
  }

  const box = parsed as Record<string, unknown>;
  if (box.track && box.track !== activeTrack) return { ok: false, reason: tr(m.otherTrack, { track: String(box.track) }) };
  if (box.kind && (box.kind !== OVERLAY_EXPORT_KIND || box.version !== OVERLAY_EXPORT_VERSION)) return { ok: false, reason: tr(m.badVersion) };
  const nested =
    box.overlay && typeof box.overlay === 'object' ? (box.overlay as Record<string, unknown>) : null;
  const candidate = nested ?? box;

  if (!looksLikeOverlay(candidate)) {
    return {
      ok: false,
      reason: tr(m.noSections),
    };
  }

  if (Array.isArray(candidate.executions)) for (const raw of candidate.executions) {
    const row = parseExecution(raw);
    if (!row) return { ok: false, reason: tr(m.execBad) };
    const reason = validateExecution?.(row);
    if (reason) return { ok: false, reason };
  }
  if (Array.isArray(candidate.assignments)) {
    const ids = new Set<string>();
    for (const raw of candidate.assignments) {
      const reason = validateAssignmentHistory(raw,snapshot);
      if (reason) return { ok: false, reason: tr(m.assignmentPrefix, { reason }) };
      const row = raw as OverlayAssignment;
      if (ids.has(row.id)) return { ok: false, reason: tr(m.assignmentDuplicate) };
      if (row.baseline && row.baseline.track !== activeTrack) return { ok: false, reason: tr(m.baselineOtherTrack) };
      ids.add(row.id);
    }
  }
  writeOverlay(parseOverlay(JSON.stringify(candidate)));
  return { ok: true };
}

/* --------------------------------------------------------------- выборки */

/**
 * Ключ, которым на запись исполнения ссылается проверка. После разбора слоя
 * `id` есть всегда; `obligation_id` остаётся запасным для записей, собранных
 * в коде мимо `parseExecution` (проверки инвариантов).
 */
export function executionRef(row: OverlayExecution): string {
  return row.id ?? row.obligation_id;
}

/**
 * Последняя по времени запись исполнения обязательства. «Последняя» — по
 * моменту ввода `at`; записи старого слоя без `at` уступают датированным,
 * между собой сохраняя порядок хранения.
 */
export function latestExecution(rows: readonly OverlayExecution[]): OverlayExecution | null {
  let best: OverlayExecution | null = null;
  for (const row of rows) {
    if (!best || (row.at ?? '') >= (best.at ?? '')) best = row;
  }
  return best;
}

/** Суждение, вынесенное именно по этой записи исполнения. Последнее — решающее. */
export function verdictOf(
  overlay: ProtoOverlay,
  execution: OverlayExecution | null,
): OverlayVerification | null {
  if (!execution) return null;
  const ref = executionRef(execution);
  let found: OverlayVerification | null = null;
  for (const row of overlay.verifications) if (row.execution_ref === ref) found = row;
  return found;
}

export function executionsByObligation(
  overlay: ProtoOverlay,
): Map<string, OverlayExecution[]> {
  const map = new Map<string, OverlayExecution[]>();
  for (const row of overlay.executions) {
    const list = map.get(row.obligation_id);
    if (list) list.push(row);
    else map.set(row.obligation_id, [row]);
  }
  return map;
}

/**
 * Проверки, относящиеся к ОДНОМУ отчёту. Без записи исполнения речь идёт об
 * отчёте среза, и ключ — сам `obligation_id`; с записью — её собственный `id`.
 * Смешивать эти два ключа нельзя: иначе суждение по отчёту среза прилипло бы
 * ещё и к сессионной записи по тому же обязательству, и один спор считался бы
 * дважды.
 */
export function verificationsFor(
  overlay: ProtoOverlay,
  obligationId: string,
  execution?: OverlayExecution | null,
): OverlayVerification[] {
  const ref = execution ? executionRef(execution) : obligationId;
  return overlay.verifications.filter((v) => v.execution_ref === ref);
}

/* ------------------------------------------------------------------ hook */

function subscribeOverlay(onChange: () => void): () => void {
  window.addEventListener(OVERLAY_EVENT, onChange);
  window.addEventListener('storage', onChange);
  return () => {
    window.removeEventListener(OVERLAY_EVENT, onChange);
    window.removeEventListener('storage', onChange);
  };
}

/**
 * Слой как источник для React: экран перерисуется на любой записи в слой.
 *
 * Третий аргумент — снимок вне браузера (SSR-прогон экранов). Там читается тот
 * же `readOverlay`: доступа к хранилищу нет, и он честно вернёт пустой слой
 * из памяти вместо исключения. Приложение поднимается через `createRoot`, так
 * что в браузере этот путь не используется вовсе.
 */
export function useOverlay(): ProtoOverlay {
  return useSyncExternalStore(subscribeOverlay, readOverlay, readOverlay);
}
